import {
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
    forwardRef,
} from 'react';
import {Button, Card, Modal} from '@goproblems/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faTrash,
    faMapPin,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import {ScenarioComment, UserMinimal, VotableEntityTypeEnum} from '@types';
import {
    useCreateScenarioCommentMutation,
    useDeleteScenarioCommentMutation,
    useFetchScenarioCommentsQuery,
    useFetchBulkVoteInfoMutation,
} from '@api';
import {useSelector} from '@utils';
import {CommentVoteButtons, MarkdownContent, MdEditor} from '@components';
import type {MdEditorHandle} from '@components/MdEditor';
import {UserItem} from './UserItem';
import RankText from './common/RankText';
import {useTranslation} from 'react-i18next';
import type {CommentVoteInfo} from '@components/CommentVoteButtons';
import {useTheme} from '@components/theme/useTheme';
import {toast} from 'react-toastify';

const A1_PATH_REGEX = /^[A-T]\d{1,2}(,[A-T]\d{1,2})+$/;

const HIGHLIGHT_DURATION = 300;

const ScenarioCommentItem = forwardRef<
    HTMLDivElement,
    {
        comment: ScenarioComment;
        canDelete?: boolean;
        onDelete?: () => void;
        onPathClick?: (path: string) => void;
        isHighlighted?: boolean;
        voteInfo?: CommentVoteInfo | null;
        onVoteChange?: (commentId: number, voteInfo: CommentVoteInfo) => void;
    }
>(
    (
        {
            comment,
            canDelete,
            onDelete,
            onPathClick,
            isHighlighted,
            voteInfo,
            onVoteChange,
        },
        ref
    ) => {
        const {t} = useTranslation();
        const theme = useTheme();
        const [showDeleteModal, setShowDeleteModal] = useState(false);
        const [isAnimating, setIsAnimating] = useState(false);
        const isDisabled = !comment.alive;
        const commentBodyRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (isHighlighted) {
                setIsAnimating(true);
                const timer = setTimeout(
                    () => setIsAnimating(false),
                    HIGHLIGHT_DURATION
                );
                return () => clearTimeout(timer);
            }
            return undefined;
        }, [isHighlighted]);

        // Re-apply styles to A1 path <code> elements after every render
        useEffect(() => {
            if (!commentBodyRef.current || !onPathClick) return;
            const codeElements =
                commentBodyRef.current.querySelectorAll('code');
            codeElements.forEach(el => {
                const text = el.textContent?.trim() ?? '';
                if (A1_PATH_REGEX.test(text)) {
                    el.classList.add('cursor-pointer');
                    el.title = t('single_problem.go_to_path', {path: text});
                }
            });
        });

        // Event delegation: handle clicks on A1 path <code> elements
        const handleBodyClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                if (!onPathClick) return;
                const target = e.target as HTMLElement;
                if (target.tagName === 'CODE') {
                    const text = target.textContent?.trim() ?? '';
                    if (A1_PATH_REGEX.test(text)) {
                        onPathClick(text);
                    }
                }
            },
            [onPathClick]
        );

        const highlightStyle = isAnimating
            ? {
                  transition: `background-color ${HIGHLIGHT_DURATION}ms ease-in-out, box-shadow ${HIGHLIGHT_DURATION}ms ease-out`,
                  backgroundColor:
                      theme === 'dark'
                          ? 'rgba(59, 130, 246, 0.15)'
                          : 'rgba(59, 130, 246, 0.1)',
                  boxShadow:
                      theme === 'dark'
                          ? '0 0 0 1px rgba(59, 130, 246, 0.4)'
                          : '0 0 0 1px rgba(59, 130, 246, 0.3)',
              }
            : {
                  transition: `background-color ${HIGHLIGHT_DURATION}ms ease-in-out, box-shadow ${HIGHLIGHT_DURATION}ms ease-out`,
              };

        return (
            <>
                <Card
                    ref={ref}
                    className="px-1.5 pt-1.5 pb-2 rounded-none"
                    style={{
                        borderTop: 'none',
                        position: 'relative',
                        ...highlightStyle,
                        ...(isDisabled && {
                            opacity: 0.65,
                            filter: 'grayscale(70%)',
                            backgroundColor:
                                theme === 'dark'
                                    ? 'rgba(255,255,255,0.02)'
                                    : 'var(--ui-surface-muted)',
                        }),
                    }}>
                    <div className="flex justify-between text-xs">
                        <UserItem
                            avatarClassName="!ml-0"
                            user={comment.author}
                            showLevel={false}
                            textClassName="!font-normal text-xs !mx-2"
                        />
                        <div className="flex gap-2.5 items-center">
                            {canDelete && comment.alive && (
                                <div className="relative group">
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        className="text-neutral-500 hover:text-red-600 cursor-pointer"
                                        onClick={() => setShowDeleteModal(true)}
                                    />
                                </div>
                            )}
                            <span>
                                <RankText
                                    rank={comment.author.displayRank}
                                    shortUnit={true}
                                    showMark={false}
                                />
                            </span>
                            <span>
                                {dayjs(comment.createdAt).format(
                                    'YYYY-MM-DD HH:mm'
                                )}
                            </span>
                        </div>
                    </div>
                    <div
                        ref={commentBodyRef}
                        onClick={handleBodyClick}
                        style={{marginTop: 8, fontSize: 12}}>
                        <MarkdownContent
                            text={comment.text}
                            preserveLineBreaks
                        />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                        <CommentVoteButtons
                            entityType={VotableEntityTypeEnum.SCENARIO_COMMENT}
                            entityId={comment.id}
                            voteInfo={voteInfo || null}
                            onVoteChange={onVoteChange}
                        />
                    </div>
                    {isDisabled && (
                        <div
                            className="absolute"
                            style={{
                                bottom: 8,
                                right: 8,
                                pointerEvents: 'none',
                                opacity: 0.5,
                            }}>
                            <FontAwesomeIcon
                                icon={faTrash}
                                style={{fontSize: 14, color: 'var(--ui-text-muted)'}}
                            />
                        </div>
                    )}
                </Card>

                <Modal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    size="sm"
                    centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="text-sm">
                            {t('single_problem.delete_comment_modal_title')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-sm">
                        {t('single_problem.delete_comment_modal_body')}
                    </Modal.Body>
                    <Modal.Footer className="text-sm">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowDeleteModal(false)}>
                            {t('single_problem.cancel')}
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                                onDelete?.();
                                setShowDeleteModal(false);
                            }}>
                            {t('single_problem.disable')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
);

ScenarioCommentItem.displayName = 'ScenarioCommentItem';

export const ScenarioCommentsCard = ({
    scenarioId,
    currentPath,
    onPathClick,
}: {
    scenarioId: number;
    currentPath?: string;
    onPathClick?: (path: string) => void;
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isDark = theme === 'dark';
    const {user} = useSelector(s => s.auth);
    const [expanded, setExpanded] = useState(false);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comments, setComments] = useState<ScenarioComment[]>([]);
    const [voteInfos, setVoteInfos] = useState<Record<number, CommentVoteInfo>>(
        {}
    );
    const scrollRef = useRef<HTMLDivElement>(null);
    const [highlightedCommentId, setHighlightedCommentId] = useState<
        number | null
    >(null);
    const newCommentRef = useRef<HTMLDivElement>(null);

    const [createComment] = useCreateScenarioCommentMutation();
    const [deleteComment] = useDeleteScenarioCommentMutation();
    const [getBulkVoteInfo] = useFetchBulkVoteInfoMutation();

    const {data: fetchedComments} = useFetchScenarioCommentsQuery(scenarioId);

    useEffect(() => {
        if (fetchedComments) {
            setComments(fetchedComments);
        }
    }, [fetchedComments]);

    const contextUsers = useMemo(() => {
        const users: UserMinimal[] = [];
        const seen = new Set<number>();
        if (comments) {
            for (let i = comments.length - 1; i >= 0; i--) {
                const author = comments[i].author;
                if (!seen.has(author.id)) {
                    seen.add(author.id);
                    users.push(author);
                }
            }
        }
        return users;
    }, [comments]);

    const commentIds = useMemo(
        () => comments?.map(c => c.id) ?? [],
        [comments]
    );

    useEffect(() => {
        const loadVoteInfos = async () => {
            if (commentIds.length === 0 || !user) return;
            try {
                const result = await getBulkVoteInfo({
                    entityType: VotableEntityTypeEnum.SCENARIO_COMMENT,
                    entityIds: commentIds,
                }).unwrap();
                setVoteInfos(result);
            } catch {
                // ignore
            }
        };
        void loadVoteInfos();
    }, [commentIds, user, getBulkVoteInfo]);

    const handleVoteChange = (commentId: number, voteInfo: CommentVoteInfo) => {
        setVoteInfos(prev => ({...prev, [commentId]: voteInfo}));
    };

    const visibleComments = comments.filter(c => c.alive);
    const aliveCount = visibleComments.length;

    const handleSubmit = async () => {
        if (!user || !comment.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const result = await createComment({
                scenarioId,
                comment,
                path: currentPath || null,
            }).unwrap();
            setComments(prev => [...prev, result]);
            setComment('');
            setHighlightedCommentId(result.id);
            // Scroll to new comment after render
            setTimeout(() => {
                newCommentRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }, 50);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (commentId: number) => {
        try {
            await deleteComment({scenarioId, commentId}).unwrap();
            setComments(prev =>
                prev.map(c => (c.id === commentId ? {...c, alive: false} : c))
            );
        } catch (error) {
            console.error('Failed to delete comment:', error);
        }
    };

    const editorRef = useRef<MdEditorHandle>(null);

    const handleInsertPath = () => {
        if (!currentPath) {
            toast.warning(
                'No path to insert. Inline path example: `C7,C9,E7,D6`',
                {
                    position: 'top-center',
                    autoClose: 3000,
                }
            );
            return;
        }
        const insertion = `\`${currentPath}\``;
        const textarea = editorRef.current?.getTextarea();
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = comment.substring(0, start);
            const after = comment.substring(end);
            const needsSpaceBefore =
                before.length > 0 &&
                !before.endsWith(' ') &&
                !before.endsWith('\n');
            const needsSpaceAfter =
                after.length === 0 ||
                (!after.startsWith(' ') && !after.startsWith('\n'));
            const newValue =
                before +
                (needsSpaceBefore ? ' ' : '') +
                insertion +
                (needsSpaceAfter ? ' ' : '') +
                after;
            setComment(newValue);
            // Restore cursor position after insertion
            const cursorPos =
                start +
                (needsSpaceBefore ? 1 : 0) +
                insertion.length +
                (needsSpaceAfter ? 1 : 0);
            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(cursorPos, cursorPos);
            });
        } else {
            setComment(prev => {
                if (prev && !prev.endsWith(' ') && !prev.endsWith('\n')) {
                    return prev + ' ' + insertion + ' ';
                }
                return prev + insertion + ' ';
            });
        }
    };

    return (
        <div
            className={`border-t pt-4 ${isDark ? 'border-neutral-700' : 'border-neutral-200'}`}>
            <button
                className="flex items-center justify-between w-full text-left"
                onClick={() => setExpanded(!expanded)}>
                <h3 className="font-semibold text-body">
                    {t('common.comments_count', {count: aliveCount})}
                </h3>
                <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    className="text-body-secondary text-sm"
                />
            </button>

            {expanded && (
                <div className="mt-3">
                    <div
                        ref={scrollRef}
                        className="overflow-y-auto"
                        style={{maxHeight: 400}}>
                        {visibleComments.length > 0 ? (
                            visibleComments.map(c => (
                                <ScenarioCommentItem
                                    key={c.id}
                                    ref={
                                        c.id === highlightedCommentId
                                            ? newCommentRef
                                            : undefined
                                    }
                                    comment={c}
                                    canDelete={user?.id === c.author.id}
                                    onDelete={() => handleDelete(c.id)}
                                    onPathClick={onPathClick}
                                    isHighlighted={
                                        c.id === highlightedCommentId
                                    }
                                    voteInfo={voteInfos[c.id]}
                                    onVoteChange={handleVoteChange}
                                />
                            ))
                        ) : (
                            <p className="text-sm text-body-secondary py-2">
                                {t('single_problem.no_comments_yet')}
                            </p>
                        )}
                    </div>

                    {/* Comment form */}
                    <div className="mt-3 text-xs">
                        {user ? (
                            <>
                                <MdEditor
                                    ref={editorRef}
                                    value={comment}
                                    onChange={setComment}
                                    placeholder={t(
                                        'single_problem.comment_placeholder'
                                    )}
                                    onKeyDown={e => {
                                        if (e.ctrlKey && e.key === 'Enter') {
                                            e.preventDefault();
                                            void handleSubmit();
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    contextUsers={contextUsers}
                                />
                                <div className="flex justify-between mt-1.5 gap-2">
                                    <button
                                        onClick={handleInsertPath}
                                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${'bg-body-secondary hover:bg-neutral-300 text-body'}`}
                                        title={
                                            currentPath
                                                ? t(
                                                      'single_problem.insert_current_path',
                                                      {path: currentPath}
                                                  )
                                                : t(
                                                      'single_problem.no_path_to_insert'
                                                  )
                                        }>
                                        <FontAwesomeIcon
                                            icon={faMapPin}
                                            className="text-xs"
                                        />
                                        {t('single_problem.insert_path')}
                                    </button>
                                    <div className="flex-1" />
                                    <Button
                                        className="text-xs"
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={
                                            isSubmitting || !comment.trim()
                                        }>
                                        {isSubmitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {t('common.submitting')}
                                            </>
                                        ) : (
                                            t('common.submit')
                                        )}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-4 text-neutral-500">
                                {t('single_problem.sign_in_to_leave_comment')}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScenarioCommentsCard;
